/**
 * SamplesCard — sample-round tracker.
 *
 * Tracks the full cut-and-sew sampling flow: Proto → Fit 1 → Fit 2 → Top
 * Sample → Size Set → Bulk (and operators can name additional rounds).
 * Each round shows requested + received dates, status pill, fit notes,
 * photo thumbnail, and approve/reject affordance.
 *
 * Approve/reject are always-on for admins (not gated by page Edit mode) —
 * they're status-advance actions that operators trigger many times per
 * day, and forcing an Edit toggle each time would add real friction.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/hooks/use-toast';
import { SampleRow } from './SampleRow';
import { CardRowsSkeleton } from './skeletons';
import { type SampleStatus } from '../utils/status-style';
import {
  useProductDevSamples,
  useProductDevMutations,
} from '../hooks/useProductDev';

interface Props {
  productDevId: string;
  canEdit: boolean;
}

const DEFAULT_ROUND_LABELS = [
  'Proto',
  'Fit 1',
  'Fit 2',
  'Fit 3',
  'Top Sample',
  'Size Set',
  'Bulk',
  'Revision',
];


export function SamplesCard({ productDevId, canEdit }: Props) {
  const { t } = useTranslation('productDev');
  const samplesQuery = useProductDevSamples(productDevId);
  const { upsertSample, deleteSample } = useProductDevMutations(productDevId);

  const rows = samplesQuery.data ?? [];
  const [addOpen, setAddOpen] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newRequestedAt, setNewRequestedAt] = useState('');
  // Round id awaiting delete confirmation (drives the AlertDialog).
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  const handleAdd = async () => {
    const label = newLabel.trim();
    if (!label) return;
    try {
      // `round_number` is allocated server-side — deriving it here from `rows`
      // read an empty list while the query was still loading and collided with
      // the round-number unique index (BLU-629 L1).
      await upsertSample.mutateAsync({
        productDevId,
        round_label: label,
        requested_at: newRequestedAt || null,
        status: 'requested',
      });
      setAddOpen(false);
      setNewLabel('');
      setNewRequestedAt('');
    } catch (err) {
      toast({
        title: t('samples.toast.addFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  /**
   * Every write below is a PATCH — send only what changed (BLU-629 H4). The
   * backend used to demand the whole row and wrote every omitted column as
   * null, so these helpers had to echo the full `row` back; two edits fired
   * before the refetch landed then clobbered each other from the same stale
   * snapshot. Don't reintroduce the echo.
   *
   * `approved_by`/`approved_at` are no longer passed at all: the server derives
   * the approval trail from the status transition, which is what makes approving
   * from this dropdown (rather than the button) record an approver at all.
   */
  const setStatus = async (row: (typeof rows)[number], status: SampleStatus) => {
    try {
      await upsertSample.mutateAsync({ id: row.id, productDevId, status });
    } catch (err) {
      toast({
        title: t('samples.toast.updateFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const patchSample = async (
    row: (typeof rows)[number],
    patch: Partial<{
      round_label: string;
      requested_at: string | null;
      received_at: string | null;
      fit_notes: string | null;
    }>,
  ) => {
    try {
      await upsertSample.mutateAsync({ id: row.id, productDevId, ...patch });
    } catch (err) {
      toast({
        title: t('samples.toast.updateFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  const confirmDelete = async () => {
    const id = pendingDeleteId;
    if (!id) return;
    setPendingDeleteId(null);
    try {
      await deleteSample.mutateAsync({ sampleId: id });
    } catch (err) {
      toast({
        title: t('samples.toast.deleteFailed'),
        description: err instanceof Error ? err.message : t('samples.toast.tryAgain'),
        variant: 'destructive',
      });
    }
  };

  return (
    <>
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-base">{t('samples.title')}</CardTitle>
        {/* Hidden while the list is still loading: adding a round used to derive
            its round_number from an empty `rows` and collide (BLU-629 L1). The
            server allocates it now, but offering Add before the rounds render is
            still a lie about what the operator is looking at. */}
        {canEdit && !addOpen && !samplesQuery.isLoading && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setAddOpen(true)}
            className="h-7 px-2 text-xs"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> {t('samples.round')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-0">
        {samplesQuery.isLoading ? (
          <div className="py-2">
            <CardRowsSkeleton rows={2} />
          </div>
        ) : rows.length === 0 && !addOpen ? (
          <p className="text-sm text-muted-foreground py-2">
            {t('samples.empty')}
          </p>
        ) : (
          // `flex-wrap` + a real min-width on the content column (inside
          // SampleRow): the Samples card lives in the detail page's LEFT column,
          // and the action cluster (Approve + Reject + status Select + delete)
          // is ~330px. As a rigid third column it squeezed the content to
          // <200px, truncating the fit notes and wrapping the QC review into a
          // narrow stack. SampleRow drops the actions to their own line when
          // they can't fit beside a readable content column, instead of
          // starving it.
          rows.map((row) => (
            <SampleRow
              key={row.id}
              row={row}
              productDevId={productDevId}
              canEdit={canEdit}
              onPatch={(r, patch) => void patchSample(r, patch)}
              onSetStatus={(r, status) => void setStatus(r, status)}
              onRequestDelete={setPendingDeleteId}
            />
          ))
        )}

        {addOpen && (
          <div className="space-y-3 pt-3 border-t mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">{t('samples.roundLabel')}</Label>
                <Select value={newLabel} onValueChange={setNewLabel}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder={t('samples.choose')} />
                  </SelectTrigger>
                  <SelectContent>
                    {DEFAULT_ROUND_LABELS.map((l) => (
                      <SelectItem key={l} value={l}>
                        {l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!DEFAULT_ROUND_LABELS.includes(newLabel) && (
                  <Input
                    placeholder={t('samples.customLabelPlaceholder')}
                    value={newLabel}
                    onChange={(e) => setNewLabel(e.target.value)}
                    className="h-8 text-sm mt-1"
                  />
                )}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t('samples.requestedDate')}</Label>
                <Input
                  type="date"
                  value={newRequestedAt}
                  onChange={(e) => setNewRequestedAt(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => {
                  setAddOpen(false);
                  setNewLabel('');
                  setNewRequestedAt('');
                }}
                className="h-7 px-2 text-xs"
              >
                {t('samples.cancel')}
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={handleAdd}
                disabled={!newLabel.trim() || upsertSample.isPending}
                className="h-7 px-2 text-xs"
              >
                {upsertSample.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <>
                    <Plus className="h-3 w-3 mr-1" /> {t('samples.addRound')}
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>

    <AlertDialog
      open={pendingDeleteId !== null}
      onOpenChange={(o) => !o && setPendingDeleteId(null)}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{t('samples.confirmDelete.title')}</AlertDialogTitle>
          <AlertDialogDescription>
            {t('samples.confirmDelete.body')}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>{t('samples.confirmDelete.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            onClick={() => void confirmDelete()}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {t('samples.confirmDelete.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
    </>
  );
}
